/* skinN°88 Curation Wizard - client-side state + rendering */
(function () {
  'use strict';

  const root = document.getElementById('curation-wizard');
  if (!root) return;

  const SETS_URL = root.dataset.setsUrl;
  const SKUS_URL = root.dataset.skusUrl;
  const META_URL = root.dataset.metaUrl;

  // ===== State =====
  const state = {
    step: 1,
    skin: [],           // ARRAY now (multi-select)
    age: null,
    mood: null,
    priority: 'match',  // 5/31 - buyer agency: match | budget | premium | value
    sets: null,
    skus: null,
    meta: null,
    currentCombo: null,
    currentPicks: [],
    swapCtx: null,
  };

  // axes vocab maps + goal signals (mirror server-side scoring)
  // Skin types are pure now (oily/dry/combo/sensitive/normal) - determined by
  // the Step 1 diagnosis quiz. Concerns/goals live in the Step 3 goal axis.
  const SKIN_TARGET = {
    oily: 'oily', dry: 'dry', sensitive: 'sensitive', combo: 'combo',
    normal: 'all',
  };
  // No longer used (concerns moved to the goal axis), kept empty so existing
  // references stay safe.
  const SKIN_EXTRA_CONCERN = {};
  const AGE_TARGET = {
    teens: 'teens', twenties_early: 'twenties', twenties_late: 'twenties',
    thirties: 'thirties', forties_plus: 'forties_plus',
  };
  // Step 3 = functional goal (replaces the old "mood" axis). Internal key name
  // stays "mood" to keep the engine/cart attributes unchanged. Each goal maps to
  // real SKU benefit (bn) + concern (co) tags so scoring routes to matching SKUs.
  const MOOD_SIGNALS = {
    hydration:   { benefit: ['hydration','plumping'], concern: ['dehydration'] },
    brightening: { benefit: ['brightening','glow'], concern: ['dullness','uneven_tone','dark_spots'] },
    soothing:    { benefit: ['soothing','redness_reduction','barrier_repair'], concern: ['redness'] },
    anti_aging:  { benefit: ['anti_aging','firming','plumping'], concern: ['fine_lines','wrinkles'] },
    pore_oil:    { benefit: ['oil_control','exfoliation'], concern: ['pores','sebum'] },
    acne_care:   { benefit: ['oil_control','soothing'], concern: ['acne','pores','sebum'] },
    barrier:     { benefit: ['barrier_repair','soothing','hydration'], concern: ['redness','dehydration'] },
    glass_skin:  { benefit: ['glow','hydration','brightening'], concern: ['glass_skin','dullness'] },
    sun_defense: { benefit: ['sun_protection','hydration'], concern: ['sun_damage'] },
    daily:       { benefit: ['hydration','soothing','sun_protection'], concern: [] },
  };

  function scoreSku(sku, skinArr, age, mood, priority) {
    let score = 0;
    const st = sku.st || [];
    const co = sku.co || [];
    const bn = sku.bn || [];
    const ag = sku.ag || [];
    for (const s of skinArr) {
      const target = SKIN_TARGET[s] || s;
      if (st.includes(target) || st.includes('all')) score += 3;
      for (const c of (SKIN_EXTRA_CONCERN[s] || [])) {
        if (co.includes(c)) score += 3;
      }
    }
    const targetAge = AGE_TARGET[age] || age;
    if (ag.includes(targetAge) || ag.includes('all_ages')) score += 2;
    const sig = MOOD_SIGNALS[mood] || {};
    for (const b of (sig.benefit || [])) if (bn.includes(b)) score += 3;
    for (const c of (sig.concern || [])) if (co.includes(c)) score += 2;

    // ===== Priority modifier (5/31 - buyer agency) =====
    // Base score range: 0-15 (skin/age/mood matches).
    // Priority weight range: 0-7. Always smaller than base score to avoid
    // overriding skin/safety matches. Default 'match' = no change.
    const p = priority || 'match';
    if (p === 'budget' && sku.p) {
      // Affordable: lower price = higher boost. $5 → +4.5 / $20 → +3 / $50 → 0
      score += Math.max(0, (50 - sku.p) * 0.1);
    } else if (p === 'premium') {
      // Premium ingredients: bonus if name matches a known active + slight price bonus
      if (sku.n && extractIngredient(sku.n)) score += 5;
      if (sku.p) score += Math.min(3, sku.p * 0.05);
    } else if (p === 'value') {
      // Value: ingredient bonus + sweet spot pricing + extreme penalty
      if (sku.n && extractIngredient(sku.n)) score += 3;
      if (sku.p >= 15 && sku.p <= 35) score += 3;
      else if (sku.p < 10 || sku.p > 50) score -= 1;
    }
    return score;
  }

  // ===== Reason generation (buyer-facing, free template) =====
  const CATEGORY_FUNCTION = {
    cleanser:  'Removes makeup, sunscreen, and impurities while keeping your skin barrier intact.',
    toner:     'Rebalances your skin and preps it to absorb the next steps.',
    essence:   'A lightweight, fast-absorbing layer that boosts hydration before serum.',
    serum:     'Targeted active treatment for your top concern.',
    ampoule:   'A concentrated serum - stronger actives, smaller bottle, faster results.',
    cream:     'Locks in everything underneath and seals hydration overnight.',
    moisture:  'A daily hydration layer for plump, calm skin.',
    mask:      'A weekly boost - deep treatment in 10-20 minutes.',
    sunscreen: 'Daily UV protection - the single most important anti-aging step.',
    eye:       'A gentler formula for the thin, delicate skin around your eyes.',
    eye_cream: 'A gentler formula for the thin, delicate skin around your eyes.',
    cushion:   'Lightweight base coverage with skincare benefits built in.',
    lip:       'Hydrates and protects the lip barrier (yes, lips have one too).',
    makeup_face: 'A finishing touch that works with - not against - your skincare.',
  };

  // Ingredient dictionary - matched against SKU name (lowercase substring).
  // Order matters: multi-word keys are checked first in extractIngredient.
  const INGREDIENT_INFO = {
    // Korean traditional botanicals
    heartleaf:   'Heartleaf (Houttuynia cordata) is a Korean botanical used for centuries to calm inflammation and redness.',
    centella:    'Centella asiatica (Cica) accelerates skin repair and soothes irritation - one of the most studied calming ingredients in K-beauty.',
    cica:        'Centella asiatica (Cica) accelerates skin repair and soothes irritation - one of the most studied calming ingredients in K-beauty.',
    mugwort:     'Mugwort is a traditional Korean herb known for its anti-inflammatory and calming properties.',
    rice:        'Fermented rice extract brightens dullness and improves skin tone - a staple of Korean skincare.',
    ginseng:     'Korean red ginseng energizes tired skin and supports firmness.',
    propolis:    'Propolis (bee resin) is antibacterial and packed with antioxidants.',
    'green tea': 'Green tea is a potent antioxidant that calms and protects against environmental stress.',
    // Modern actives
    pdrn:         'PDRN (Polydeoxyribonucleotide) stimulates cellular regeneration - originally a medical-grade ingredient now in K-beauty serums.',
    snail:        'Snail mucin promotes skin repair, deep hydration, and a healthier-looking barrier.',
    niacinamide:  'Niacinamide (vitamin B3) brightens, controls oil, and minimizes the appearance of pores.',
    retinol:      'Retinol accelerates cell turnover and firms over time - introduce slowly.',
    bakuchiol:    'Bakuchiol is a plant-based retinol alternative - similar firming benefits, none of the irritation.',
    'vitamin c':  'Vitamin C brightens dullness and fades dark spots over time.',
    peptide:      'Peptides signal your skin to produce more collagen, supporting firmness.',
    collagen:     'Collagen supports skin firmness and elasticity.',
    ceramide:     'Ceramides rebuild the skin barrier so moisture stays in and irritants stay out.',
    squalane:     'Squalane (plant-derived) deeply moisturizes without clogging pores.',
    hyaluronic:   'Hyaluronic acid binds water to your skin - the gold standard for plump hydration.',
    panthenol:    'Panthenol (provitamin B5) soothes, hydrates, and supports healing.',
    azelaic:      'Azelaic acid calms redness, fades dark spots, and helps with acne.',
    glycolic:     'Glycolic acid gently exfoliates dead surface cells to reveal smoother skin.',
    salicylic:    'Salicylic acid penetrates pores to clear sebum and prevent breakouts.',
    tranexamic:   'Tranexamic acid is one of the most effective ingredients for melasma and stubborn dark spots.',
    idebenone:    'Idebenone is a powerful antioxidant - up to 10x stronger than vitamin E in protecting skin from damage.',
    arbutin:      'Arbutin gently fades hyperpigmentation without the harshness of hydroquinone.',
    'rose hip':   'Rosehip oil is rich in vitamin C and essential fatty acids - brightens and softens.',
    'tea tree':   'Tea tree oil is naturally antibacterial - a classic for blemish-prone skin.',
    madecassoside: 'Madecassoside (from Centella) is a potent soothing molecule that reduces redness fast.',
  };

  const BENEFIT_LABELS = {
    glow:              'a luminous, healthy glow',
    brightening:       'brightening dullness',
    hydration:         'deep hydration',
    plumping:          'plumping the skin',
    soothing:          'calming redness and reactivity',
    anti_aging:        'anti-aging support',
    firming:           'firming and toning',
    barrier_repair:    'rebuilding the skin barrier',
    sun_protection:    'daily sun defense',
    oil_control:       'balancing excess oil',
    exfoliation:       'gentle exfoliation',
    redness_reduction: 'reducing redness',
  };

  const CONCERN_LABELS = {
    acne:        'breakouts',
    pores:       'visible pores',
    sebum:       'excess oil',
    dark_spots:  'dark spots',
    uneven_tone: 'uneven tone',
    sun_damage:  'sun damage',
    dullness:    'dullness',
    redness:     'redness and irritation',
    dehydration: 'dehydration',
    fine_lines:  'fine lines',
    wrinkles:    'wrinkles',
    glass_skin:  'that glass-skin finish',
  };

  function extractIngredient(skuName) {
    if (!skuName) return null;
    const lower = skuName.toLowerCase();
    // Multi-word first (longer match wins)
    const multiWord = ['vitamin c', 'hyaluronic', 'tranexamic', 'glycolic',
                       'salicylic', 'azelaic', 'rose hip', 'tea tree',
                       'green tea', 'madecassoside'];
    for (const key of multiWord) {
      if (lower.includes(key)) return key;
    }
    for (const key of Object.keys(INGREDIENT_INFO)) {
      if (lower.includes(key)) return key;
    }
    return null;
  }

  function buildReason(sku, skinArr, age, mood) {
    const category = (sku.c || '').toLowerCase();
    const whatItDoes = CATEGORY_FUNCTION[category] || 'Part of your curated routine.';

    const ingredient = extractIngredient(sku.n);
    const howItWorks = ingredient ? INGREDIENT_INFO[ingredient] : null;

    const matches = [];
    const st = sku.st || [];
    const co = sku.co || [];
    const bn = sku.bn || [];
    const ag = sku.ag || [];

    // Skin type match (1 line max)
    for (const s of skinArr) {
      const target = SKIN_TARGET[s] || s;
      if (st.includes(target) || st.includes('all')) {
        matches.push(`Matched to your ${labelForSkin(s).toLowerCase()} skin`);
        break;
      }
    }
    // Age match
    const targetAge = AGE_TARGET[age] || age;
    if (ag.includes(targetAge)) {
      matches.push(`Picked for your ${labelForAge(age)} routine`);
    }
    // Goal benefit match
    const sig = MOOD_SIGNALS[mood] || {};
    const goalLabel = labelForMood(mood).toLowerCase();
    let benefitAdded = false;
    for (const b of (sig.benefit || [])) {
      if (bn.includes(b) && !benefitAdded) {
        const label = BENEFIT_LABELS[b] || b.replace(/_/g, ' ');
        matches.push(`Supports ${label} for your "${goalLabel}" goal`);
        benefitAdded = true;
      }
    }
    // Goal concern match
    for (const c of (sig.concern || [])) {
      if (co.includes(c)) {
        matches.push(`Targets ${CONCERN_LABELS[c] || c.replace(/_/g, ' ')}`);
        break;
      }
    }

    return {
      whatItDoes,
      howItWorks,
      whyForYou: matches.slice(0, 3),
    };
  }

  // ===== Dynamic slot selection (3-8 slots based on match strength) =====
  const SLOT_POOL = ['cleanser', 'toner', 'essence', 'serum', 'ampoule',
                     'cream', 'mask', 'sunscreen'];
  const REQUIRED_SLOTS = ['cleanser', 'toner', 'cream'];
  const SCORE_THRESHOLD = 5;
  const MIN_SLOTS = 3;
  const MAX_SLOTS = 8;

  function pickPerSlotDynamic(skinArr, age, mood, priority) {
    const used_brands = {};

    // Step 1: For each candidate slot, find best SKU + score
    const slotResults = [];
    for (const slot of SLOT_POOL) {
      const candidates = Object.entries(state.skus)
        .filter(([hid, s]) => (s.c || '').toLowerCase() === slot)
        .map(([hid, s]) => ({
          hid, sku: s, score: scoreSku(s, skinArr, age, mood, priority)
        }))
        .sort((a, b) => b.score - a.score);
      if (candidates.length === 0) continue;
      slotResults.push({ slot, candidates });
    }

    // Step 2: Sort slots by best candidate's score, descending
    slotResults.sort((a, b) => b.candidates[0].score - a.candidates[0].score);

    // Step 3: Required slots first (always include if SKU exists)
    const picks = [];
    const usedSlots = new Set();
    for (const reqSlot of REQUIRED_SLOTS) {
      const res = slotResults.find(r => r.slot === reqSlot);
      if (!res) continue;
      const cand = res.candidates.map(c => ({
        ...c, score: c.score - 2 * (used_brands[c.sku.b] || 0)
      })).sort((a, b) => b.score - a.score)[0];
      used_brands[cand.sku.b] = (used_brands[cand.sku.b] || 0) + 1;
      picks.push({ slot: reqSlot, hid: cand.hid });
      usedSlots.add(reqSlot);
    }

    // Step 4: Optional slots that pass score threshold
    for (const res of slotResults) {
      if (usedSlots.has(res.slot)) continue;
      if (picks.length >= MAX_SLOTS) break;
      // Apply brand penalty before final comparison
      const cand = res.candidates.map(c => ({
        ...c, score: c.score - 2 * (used_brands[c.sku.b] || 0)
      })).sort((a, b) => b.score - a.score)[0];
      // Stop adding optional slots once we have minimum + score drops below threshold
      if (cand.score < SCORE_THRESHOLD && picks.length >= MIN_SLOTS) break;
      used_brands[cand.sku.b] = (used_brands[cand.sku.b] || 0) + 1;
      picks.push({ slot: res.slot, hid: cand.hid });
      usedSlots.add(res.slot);
    }

    // Step 5: Reorder picks to a sensible routine order
    const ROUTINE_ORDER = ['cleanser', 'toner', 'essence', 'ampoule', 'serum',
                           'mask', 'cream', 'sunscreen'];
    picks.sort((a, b) => {
      const ai = ROUTINE_ORDER.indexOf(a.slot);
      const bi = ROUTINE_ORDER.indexOf(b.slot);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return picks;
  }

  function discountFor(n) {
    if (!state.meta) return 0;
    if (n <= 0) return 0;
    if (n <= 8) return state.meta.discount[String(n)] ?? 0;
    return state.meta.discount_cap ?? 0.30;
  }

  // ===== Load data =====
  Promise.all([
    fetch(SETS_URL).then(r => r.json()),
    fetch(SKUS_URL).then(r => r.json()),
    fetch(META_URL).then(r => r.json()),
  ]).then(([sets, skus, meta]) => {
    state.sets = sets;
    state.skus = skus;
    state.meta = meta;
    renderAxesOptions();
    setupFacemapBridge();
    bindPriorityToggle();
    updateStepper();
  }).catch(err => {
    console.error('Curation Wizard load failed', err);
  });

  // ===== Priority toggle (buyer agency, 5/31) =====
  function bindPriorityToggle() {
    const buttons = root.querySelectorAll('.cw-priority-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const p = btn.dataset.priority;
        if (state.priority === p) return;
        state.priority = p;
        buttons.forEach(b => b.classList.toggle('cw-priority-active',
                                                b.dataset.priority === p));
        if (state.step === 4) {
          renderResult();
        }
      });
    });
  }

  // ===== Render option cards =====
  function renderAxesOptions() {
    const skinEl = document.getElementById('cw-skin-options');
    const ageEl = document.getElementById('cw-age-options');
    const moodEl = document.getElementById('cw-mood-options');

    skinEl.innerHTML = state.meta.axes.skin.map(o => `
      <div class="cw-option" data-axis="skin" data-key="${o.k}">${o.label}</div>
    `).join('');
    ageEl.innerHTML = state.meta.axes.age.map(o => `
      <div class="cw-option" data-axis="age" data-key="${o.k}">${o.label}</div>
    `).join('');
    moodEl.innerHTML = state.meta.axes.mood.map(o => `
      <div class="cw-option" data-axis="mood" data-key="${o.k}">${o.label}</div>
    `).join('');

    // Click handlers
    root.querySelectorAll('.cw-option').forEach(el => {
      el.addEventListener('click', () => {
        const axis = el.dataset.axis;
        const key = el.dataset.key;
        if (axis === 'skin') {
          // MULTI-select: toggle
          const idx = state.skin.indexOf(key);
          if (idx >= 0) state.skin.splice(idx, 1);
          else state.skin.push(key);
          el.classList.toggle('cw-option-selected');
          updateSkinNextBtn();
          return;
        }
        // age / mood: SINGLE-select with auto-advance
        state[axis] = key;
        root.querySelectorAll(`.cw-option[data-axis="${axis}"]`)
          .forEach(o => o.classList.remove('cw-option-selected'));
        el.classList.add('cw-option-selected');
        setTimeout(() => goToStep(state.step + 1), 250);
      });
    });

    // Skin "Next" button
    const skinNext = document.getElementById('cw-skin-next');
    if (skinNext) {
      skinNext.addEventListener('click', () => {
        if (state.skin.length > 0) goToStep(2);
      });
    }
  }

  // ===== Step 1 diagnosis quiz =====
  // Behavioral questions (CeraVe "30 min after cleanse" + Baumann oily/dry &
  // sensitive/resistant axes) that determine skin type instead of asking the
  // buyer to guess. Output fills state.skin with oily/dry/combo/sensitive/normal.
  const DIAGNOSIS = [
    {
      q: 'How does your skin feel about 30 minutes after washing, with nothing applied?',
      opts: [
        { label: 'Tight, dry, maybe a little flaky all over', score: { dry: 2 } },
        { label: 'Comfortable and balanced', score: { normal: 2 } },
        { label: 'Shiny on the T-zone, normal or dry on the cheeks', score: { combo: 2 } },
        { label: 'Shiny and greasy all over', score: { oily: 2 } },
      ],
    },
    {
      q: 'By the middle of the afternoon, your T-zone (forehead and nose)...',
      opts: [
        { label: 'Gets oily and shiny', score: { oily: 2 } },
        { label: 'Only the T-zone gets oily, cheeks stay dry', score: { combo: 2 } },
        { label: 'Stays dry or slightly flaky', score: { dry: 2 } },
        { label: 'Looks about the same as the morning', score: { normal: 1 } },
      ],
    },
    {
      q: 'Your pores are...',
      opts: [
        { label: 'Large and visible, especially around the nose', score: { oily: 1, combo: 1 } },
        { label: 'Visible mostly around the T-zone', score: { combo: 1 } },
        { label: 'Small, barely noticeable', score: { dry: 1, normal: 1 } },
        { label: 'Normal, nothing I really notice', score: { normal: 1 } },
      ],
    },
    {
      q: 'When you try a new product, or face cold or hot weather, your skin...',
      opts: [
        { label: 'Often stings, turns red, or itches', score: { sensitive: 3 } },
        { label: 'Reacts every now and then', score: { sensitive: 1 } },
        { label: 'Rarely reacts', score: {} },
        { label: 'Never gives me any trouble', score: {} },
      ],
    },
    {
      q: 'Most weeks, your skin tends to...',
      opts: [
        { label: 'Break out or get congested', score: { oily: 1 } },
        { label: 'Feel dry, tight, or rough', score: { dry: 1 } },
        { label: 'Be oily in some spots and dry in others', score: { combo: 1 } },
        { label: 'Stay pretty stable', score: { normal: 1 } },
      ],
    },
  ];
  let quizIdx = 0;

  function setupDiagnosis() {
    const skipBtn = document.getElementById('cw-quiz-skip');
    const returnBtn = document.getElementById('cw-quiz-return');
    const backBtn = document.getElementById('cw-quiz-back');
    if (skipBtn) skipBtn.addEventListener('click', showSelfSelect);
    if (returnBtn) returnBtn.addEventListener('click', showQuiz);
    if (backBtn) backBtn.addEventListener('click', quizBack);
    renderQuizQuestion();
  }

  function showSelfSelect() {
    const fm = document.getElementById('cw-facemap');
    const self = document.getElementById('cw-skin-self');
    if (fm) fm.style.display = 'none';
    if (self) self.style.display = 'block';
  }

  function showFacemap() {
    const fm = document.getElementById('cw-facemap');
    const self = document.getElementById('cw-skin-self');
    if (self) self.style.display = 'none';
    if (fm) fm.style.display = 'block';
  }

  // Bridge: face-map (curation-facemap.js) -> wizard state (LOCKED contract:
  // cw:diagnosis-complete {skinType:[], zoneMap:{}, goalHint, skip}).
  function setupFacemapBridge() {
    document.addEventListener('cw:diagnosis-complete', function (e) {
      const d = (e && e.detail) || {};
      if (d.skip || !Array.isArray(d.skinType) || d.skinType.length === 0) {
        showSelfSelect();          // buyer chose "pick manually" / "just recommend"
        return;
      }
      state.skin = d.skinType.slice();
      state._goalHint = d.goalHint || null;
      state._zoneMap = d.zoneMap || {};
      syncSelfSelectFromState();   // keep fallback in sync if they tap Back
      goToStep(2);
    });
  }

  // dry/flaky majority -> gently suggest the Hydration goal on Step 3 (buyer still chooses)
  function applyGoalHint() {
    if (!state._goalHint) return;
    root.querySelectorAll('#cw-mood-options .cw-option').forEach(function (el) {
      el.classList.toggle('cw-option-suggested', el.dataset.key === state._goalHint);
    });
  }

  function showQuiz() {
    const diag = document.getElementById('cw-diagnosis');
    const self = document.getElementById('cw-skin-self');
    if (self) self.style.display = 'none';
    if (diag) diag.style.display = 'block';
    renderQuizQuestion();
  }

  function resetQuiz() {
    DIAGNOSIS.forEach(qq => { qq._answer = null; });
    quizIdx = 0;
    showQuiz();
  }

  function quizBack() {
    if (quizIdx > 0) { quizIdx--; renderQuizQuestion(); }
  }

  function renderQuizQuestion() {
    const body = document.getElementById('cw-quiz-body');
    const prog = document.getElementById('cw-quiz-progress');
    const backBtn = document.getElementById('cw-quiz-back');
    if (!body) return;
    const q = DIAGNOSIS[quizIdx];
    if (prog) prog.textContent = `Question ${quizIdx + 1} of ${DIAGNOSIS.length}`;
    if (backBtn) backBtn.style.display = quizIdx > 0 ? 'inline-flex' : 'none';
    body.innerHTML = `
      <h4 class="cw-quiz-q">${q.q}</h4>
      <div class="cw-quiz-opts">
        ${q.opts.map((o, i) => `
          <button type="button" class="cw-quiz-opt ${q._answer === i ? 'cw-quiz-opt-active' : ''}" data-i="${i}">${o.label}</button>
        `).join('')}
      </div>
    `;
    body.querySelectorAll('.cw-quiz-opt').forEach(btn => {
      btn.addEventListener('click', () => answerQuiz(parseInt(btn.dataset.i)));
    });
  }

  function answerQuiz(optIdx) {
    DIAGNOSIS[quizIdx]._answer = optIdx;
    quizIdx++;
    if (quizIdx < DIAGNOSIS.length) {
      renderQuizQuestion();
    } else {
      computeAndShowResult();
    }
  }

  function computeAndShowResult() {
    const scores = { oily: 0, dry: 0, combo: 0, normal: 0, sensitive: 0 };
    DIAGNOSIS.forEach(q => {
      if (q._answer == null) return;
      const sc = q.opts[q._answer].score || {};
      for (const k in sc) scores[k] = (scores[k] || 0) + sc[k];
    });
    let primary = 'normal', best = -1;
    for (const k of ['oily', 'dry', 'combo', 'normal']) {
      if (scores[k] > best) { best = scores[k]; primary = k; }
    }
    const result = [primary];
    if (scores.sensitive >= 3) result.push('sensitive');
    state.skin = result;
    renderQuizResult(result);
  }

  function renderQuizResult(result) {
    const body = document.getElementById('cw-quiz-body');
    const prog = document.getElementById('cw-quiz-progress');
    const backBtn = document.getElementById('cw-quiz-back');
    if (!body) return;
    if (prog) prog.textContent = 'Your result';
    if (backBtn) backBtn.style.display = 'inline-flex';
    const labels = result.map(k => labelForSkin(k)).join(' + ');
    body.innerHTML = `
      <div class="cw-quiz-result">
        <div class="cw-quiz-result-label">Your skin looks like</div>
        <div class="cw-quiz-result-type">${labels}</div>
        <p class="cw-quiz-result-note">We'll build your routine around this. You can still fine-tune everything on the next steps.</p>
        <button type="button" class="cw-btn cw-btn-primary" id="cw-quiz-continue">Continue →</button>
        <button type="button" class="cw-quiz-skip" id="cw-quiz-manual">Not quite? Pick manually</button>
      </div>
    `;
    const cont = document.getElementById('cw-quiz-continue');
    const manual = document.getElementById('cw-quiz-manual');
    if (cont) cont.addEventListener('click', () => goToStep(2));
    if (manual) manual.addEventListener('click', () => {
      showSelfSelect();
      syncSelfSelectFromState();
    });
  }

  function syncSelfSelectFromState() {
    root.querySelectorAll('#cw-skin-options .cw-option').forEach(el => {
      el.classList.toggle('cw-option-selected', state.skin.includes(el.dataset.key));
    });
    updateSkinNextBtn();
  }

  function updateSkinNextBtn() {
    const btn = document.getElementById('cw-skin-next');
    if (!btn) return;
    const n = state.skin.length;
    btn.disabled = n === 0;
    btn.textContent = n === 0
      ? 'Pick at least one'
      : n === 1 ? `Next →` : `Next (${n} selected) →`;
  }

  // ===== Step navigation =====
  function goToStep(n) {
    if (n < 1 || n > 4) return;
    if (n === 4 && (!state.skin || !state.age || !state.mood)) return;
    state.step = n;
    root.querySelectorAll('.cw-panel').forEach(p => {
      p.classList.toggle('cw-panel-active',
        parseInt(p.dataset.panel) === n);
    });
    updateStepper();
    if (n === 3) applyGoalHint();
    if (n === 4) renderResult();
  }

  function updateStepper() {
    root.querySelectorAll('.cw-step-pill').forEach(p => {
      const s = parseInt(p.dataset.step);
      p.removeAttribute('data-active');
      p.removeAttribute('data-done');
      if (s === state.step) p.setAttribute('data-active', '');
      else if (s < state.step) p.setAttribute('data-done', '');
    });
  }

  // back buttons
  root.querySelectorAll('.cw-back-btn').forEach(b => {
    b.addEventListener('click', () => {
      const back = parseInt(b.dataset.back);
      if (back === 1) {
        // "Start Over" - reset state + diagnosis quiz
        state.skin = [];
        state.age = null;
        state.mood = null;
        state._goalHint = null;
        root.querySelectorAll('.cw-option-selected')
          .forEach(el => el.classList.remove('cw-option-selected'));
        updateSkinNextBtn();
        document.dispatchEvent(new CustomEvent('cw:facemap-reset'));
        showFacemap();
      }
      goToStep(back);
    });
  });

  // ===== Render result panel =====
  function renderResult() {
    const skinArr = state.skin;
    if (skinArr.length === 0) {
      document.getElementById('cw-set-name').textContent = 'No selection';
      document.getElementById('cw-set-blurb').textContent = 'Please go back and pick.';
      document.getElementById('cw-slots').innerHTML = '';
      return;
    }

    // Dynamic slot algorithm: picks 3-8 slots based on match strength,
    // unified across single-skin and multi-skin paths.
    // state.priority (match | budget | premium | value) shapes tie-breaking.
    const picks = pickPerSlotDynamic(skinArr, state.age, state.mood, state.priority);
    state.currentPicks = picks.map(p => ({ ...p, removed: false }));

    // Name + blurb: prefer AI-curated copy for single-skin combos, fallback to generic
    const baseComboId = `${skinArr[0]}_${state.age}_${state.mood}`;
    const baseCombo = state.sets[baseComboId];
    if (skinArr.length === 1 && baseCombo) {
      document.getElementById('cw-set-name').textContent = baseCombo.name;
      document.getElementById('cw-set-blurb').textContent = baseCombo.blurb;
    } else {
      const skinLabels = skinArr.map(s => labelForSkin(s)).join(' + ');
      document.getElementById('cw-set-name').textContent = `Curated for ${skinLabels}`;
      document.getElementById('cw-set-blurb').textContent =
        `Hand-picked for ${skinLabels.toLowerCase()} skin, in your ${labelForAge(state.age)}, focused on ${labelForMood(state.mood).toLowerCase()}.`;
    }
    renderSlots();
  }

  function pickPerSlot(slots, skinArr, age, mood) {
    const used_brands = {};
    const picks = [];
    for (const slot of slots) {
      const candidates = Object.entries(state.skus)
        .filter(([hid, s]) => (s.c || '').toLowerCase() === slot)
        .map(([hid, s]) => {
          const score = scoreSku(s, skinArr, age, mood);
          const penalty = 2 * (used_brands[s.b] || 0);
          return { hid, score: score - penalty, sku: s };
        })
        .sort((a, b) => b.score - a.score);
      if (candidates.length === 0) continue;
      const best = candidates[0];
      used_brands[best.sku.b] = (used_brands[best.sku.b] || 0) + 1;
      picks.push({ slot, hid: best.hid });
    }
    return picks;
  }

  function labelForSkin(key) {
    const item = (state.meta.axes.skin || []).find(o => o.k === key);
    return item ? item.label : key;
  }
  function labelForAge(key) {
    const item = (state.meta.axes.age || []).find(o => o.k === key);
    return item ? item.label : key;
  }
  function labelForMood(key) {
    const item = (state.meta.axes.mood || []).find(o => o.k === key);
    return item ? item.label : key;
  }

  function renderSlots() {
    const wrap = document.getElementById('cw-slots');
    wrap.innerHTML = state.currentPicks.map((pick, idx) => {
      const sku = state.skus[pick.hid];
      if (!sku) return '';
      const imgHtml = sku.i
        ? `<img src="${sku.i}" alt="${sku.n}" loading="lazy">`
        : `<div style="color:#c8a87c;font-size:0.8rem">${pick.slot}</div>`;
      const detailUrl = sku.h ? `/products/${sku.h}` : null;
      const imageWrap = detailUrl
        ? `<a class="cw-slot-image cw-slot-image-link" href="${detailUrl}" target="_blank" rel="noopener" title="View product details">${imgHtml}</a>`
        : `<div class="cw-slot-image">${imgHtml}</div>`;
      const nameWrap = detailUrl
        ? `<a class="cw-slot-name cw-slot-name-link" href="${detailUrl}" target="_blank" rel="noopener">${(sku.n || '').slice(0, 60)}</a>`
        : `<div class="cw-slot-name">${(sku.n || '').slice(0, 60)}</div>`;

      const reason = buildReason(sku, state.skin, state.age, state.mood);
      const reasonHtml = `
        <div class="cw-slot-reason">
          <button type="button" class="cw-reason-toggle" data-action="reason" data-idx="${idx}" aria-expanded="false">
            <span>Why we picked this</span>
            <span class="cw-reason-arrow">▼</span>
          </button>
          <div class="cw-reason-content" hidden>
            <div class="cw-reason-section">
              <div class="cw-reason-label">What it does</div>
              <div class="cw-reason-text">${reason.whatItDoes}</div>
            </div>
            ${reason.howItWorks ? `
              <div class="cw-reason-section">
                <div class="cw-reason-label">How it works</div>
                <div class="cw-reason-text">${reason.howItWorks}</div>
              </div>
            ` : ''}
            ${reason.whyForYou.length > 0 ? `
              <div class="cw-reason-section">
                <div class="cw-reason-label">Why for you</div>
                <ul class="cw-reason-list">
                  ${reason.whyForYou.map(m => `<li>${m}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
        </div>
      `;

      return `
        <div class="cw-slot-card ${pick.removed ? 'cw-slot-removed' : ''}" data-idx="${idx}">
          ${imageWrap}
          <div class="cw-slot-category">${pick.slot.replace(/_/g, ' ')}</div>
          <div class="cw-slot-brand">${sku.b || ''}</div>
          ${nameWrap}
          <div class="cw-slot-price">$${sku.p.toFixed(2)}</div>
          ${reasonHtml}
          <div class="cw-slot-actions">
            <button class="cw-slot-btn cw-slot-btn-swap" data-action="swap" data-idx="${idx}">Swap</button>
            <button class="cw-slot-btn cw-slot-btn-remove" data-action="toggle" data-idx="${idx}" title="${pick.removed ? 'Add back' : 'Remove'}">${pick.removed ? '+' : '×'}</button>
          </div>
        </div>
      `;
    }).join('');

    // Append "Add another item" tile
    wrap.insertAdjacentHTML('beforeend', `
      <button type="button" class="cw-slot-add-card" id="cw-slot-add-card">
        <div class="cw-slot-add-icon">+</div>
        <div class="cw-slot-add-text">Add another item</div>
      </button>
    `);

    // Bind slot actions
    wrap.querySelectorAll('[data-action="toggle"]').forEach(b => {
      b.addEventListener('click', () => {
        const idx = parseInt(b.dataset.idx);
        state.currentPicks[idx].removed = !state.currentPicks[idx].removed;
        renderSlots();
        renderSummary();
      });
    });
    wrap.querySelectorAll('[data-action="swap"]').forEach(b => {
      b.addEventListener('click', () => openSwap(parseInt(b.dataset.idx)));
    });
    // Reason expand/collapse toggle
    wrap.querySelectorAll('[data-action="reason"]').forEach(b => {
      b.addEventListener('click', () => {
        const content = b.nextElementSibling;
        const arrow = b.querySelector('.cw-reason-arrow');
        const expanded = b.getAttribute('aria-expanded') === 'true';
        b.setAttribute('aria-expanded', String(!expanded));
        if (expanded) {
          content.setAttribute('hidden', '');
          if (arrow) arrow.textContent = '▼';
        } else {
          content.removeAttribute('hidden');
          if (arrow) arrow.textContent = '▲';
        }
      });
    });
    const addBtn = document.getElementById('cw-slot-add-card');
    if (addBtn) addBtn.addEventListener('click', openAddSlot);

    renderSummary();
  }

  function renderSummary() {
    const active = state.currentPicks.filter(p => !p.removed);
    const subtotal = active.reduce((sum, p) => sum + (state.skus[p.hid]?.p || 0), 0);
    const n = active.length;
    const discountRate = discountFor(n);
    const discount = subtotal * discountRate;
    const total = subtotal - discount;
    const finalTotal = total;

    document.getElementById('cw-summary-count').textContent = `${n} item${n !== 1 ? 's' : ''}`;
    document.getElementById('cw-summary-subtotal').textContent = `$${subtotal.toFixed(2)}`;
    document.getElementById('cw-summary-disc-pct').textContent = `${Math.round(discountRate * 100)}%`;
    document.getElementById('cw-summary-discount').textContent = `-$${discount.toFixed(2)}`;
    document.getElementById('cw-summary-shipping').textContent = 'Calculated at checkout';
    document.getElementById('cw-summary-total').textContent = `$${finalTotal.toFixed(2)}`;

    // Discount nudge banner (dynamic, based on state.meta.discount table)
    const nudgeEl = document.getElementById('cw-free-shipping-nudge');
    if (nudgeEl) {
      nudgeEl.classList.remove('cw-nudge-unlocked');

      // Live model: 20% off the set at 5+ items, none below. Single items use the 10% welcome code.
      if (n === 0) {
        nudgeEl.style.display = 'none';
      } else if (n >= 5) {
        nudgeEl.textContent = `20% off applied - your full set discount is unlocked`;
        nudgeEl.classList.add('cw-nudge-unlocked');
        nudgeEl.style.display = 'block';
      } else {
        const need = 5 - n;
        nudgeEl.textContent = `Add ${need} more item${need > 1 ? 's' : ''} to unlock 20% off your set`;
        nudgeEl.style.display = 'block';
      }
    }
  }

  // ===== Drawer (swap / add) =====
  function openDrawer() {
    document.getElementById('cw-swap-drawer').setAttribute('aria-hidden', 'false');
    const b = document.getElementById('cw-swap-backdrop');
    if (b) b.setAttribute('aria-hidden', 'false');
  }

  function openSwap(idx) {
    const pick = state.currentPicks[idx];
    state.swapCtx = { mode: 'swap', targetIdx: idx, category: pick.slot };
    document.querySelector('.cw-swap-header h4').textContent = `Swap this ${pick.slot}`;
    renderSkuPickerList(pick.slot, pick.hid);
    openDrawer();
  }

  function openAddSlot() {
    state.swapCtx = { mode: 'add-cat' };
    document.querySelector('.cw-swap-header h4').textContent = 'Add another item';
    renderCategoryPickerList();
    openDrawer();
  }

  function renderCategoryPickerList() {
    const cats = (state.meta && state.meta.categories) || [];
    const list = document.getElementById('cw-swap-list');
    list.innerHTML = `
      <p class="cw-drawer-hint">Pick a category to add</p>
      <div class="cw-cat-grid">
        ${cats.map(c => `<button type="button" class="cw-cat-pill" data-cat="${c}">${labelForCat(c)}</button>`).join('')}
      </div>
    `;
    list.querySelectorAll('.cw-cat-pill').forEach(b => {
      b.addEventListener('click', () => {
        const cat = b.dataset.cat;
        state.swapCtx = { mode: 'add-sku', category: cat };
        document.querySelector('.cw-swap-header h4').textContent = `Pick a ${labelForCat(cat)}`;
        renderSkuPickerList(cat, null);
      });
    });
  }

  function labelForCat(c) {
    return c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
  }

  function renderSkuPickerList(category, excludeHid) {
    const candidates = Object.entries(state.skus)
      .filter(([hid, s]) => (s.c || '').toLowerCase() === category && hid !== excludeHid)
      .map(([hid, s]) => ({ hid, ...s }))
      .slice(0, 12);

    const list = document.getElementById('cw-swap-list');
    list.innerHTML = candidates.length
      ? candidates.map(c => {
          const detail = c.h ? `<a class="cw-swap-item-detail" href="/products/${c.h}" target="_blank" rel="noopener" title="View details">↗</a>` : '';
          return `
            <div class="cw-swap-item" data-hid="${c.hid}">
              <img src="${c.i || ''}" alt="${c.n || ''}" loading="lazy">
              <div class="cw-swap-item-info">
                <div class="cw-swap-item-brand">${c.b || ''}</div>
                <div class="cw-swap-item-name">${(c.n || '').slice(0, 50)}</div>
                <div class="cw-swap-item-price">$${c.p.toFixed(2)}</div>
              </div>
              ${detail}
            </div>
          `;
        }).join('')
      : '<p style="color:#888;padding:20px">No products in this category yet.</p>';

    list.querySelectorAll('.cw-swap-item').forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.target.closest('.cw-swap-item-detail')) return;
        const newHid = item.dataset.hid;
        if (state.swapCtx.mode === 'swap') {
          const idx = state.swapCtx.targetIdx;
          state.currentPicks[idx].hid = newHid;
          state.currentPicks[idx].removed = false;
        } else if (state.swapCtx.mode === 'add-sku') {
          state.currentPicks.push({
            slot: state.swapCtx.category,
            hid: newHid,
            removed: false,
          });
        }
        closeSwap();
        renderSlots();
      });
    });
  }

  // Inject backdrop into <body> with INLINE styles so ancestor CSS / transforms
  // can't break its fixed positioning or visibility.
  let backdropEl = document.getElementById('cw-swap-backdrop');
  if (!backdropEl) {
    backdropEl = document.createElement('div');
    backdropEl.id = 'cw-swap-backdrop';
    backdropEl.className = 'cw-swap-backdrop';
    backdropEl.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'background:rgba(26,26,26,0.45)', 'z-index:99998',
      'opacity:0', 'pointer-events:none',
      'transition:opacity 0.25s ease',
    ].join(';');
    document.body.appendChild(backdropEl);
  }
  // Move drawer to <body> too + ensure high z-index inline
  const drawerEl = document.getElementById('cw-swap-drawer');
  if (drawerEl && drawerEl.parentElement !== document.body) {
    document.body.appendChild(drawerEl);
  }
  if (drawerEl) drawerEl.style.zIndex = '99999';

  function outsideClickHandler(e) {
    if (drawerEl.contains(e.target)) return;
    closeSwap();
  }

  function openDrawer() {
    drawerEl.setAttribute('aria-hidden', 'false');
    backdropEl.style.opacity = '1';
    backdropEl.style.pointerEvents = 'auto';
    // Attach outside-click listener on next tick (so the click that opened
    // the drawer doesn't immediately close it via bubbling)
    setTimeout(() => document.addEventListener('click', outsideClickHandler), 0);
  }

  function closeSwap() {
    drawerEl.setAttribute('aria-hidden', 'true');
    backdropEl.style.opacity = '0';
    backdropEl.style.pointerEvents = 'none';
    document.removeEventListener('click', outsideClickHandler);
    state.swapCtx = null;
  }

  document.getElementById('cw-swap-close').addEventListener('click', (e) => {
    e.stopPropagation();
    closeSwap();
  });
  backdropEl.addEventListener('click', closeSwap);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawerEl.getAttribute('aria-hidden') === 'false') {
      closeSwap();
    }
  });

  // ===== Add to cart (v2 - real Shopify integration) =====
  async function fetchVariantId(handle) {
    const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`Product fetch failed: ${handle} (${r.status})`);
    const product = await r.json();
    const available = product.variants.find(v => v.available) || product.variants[0];
    if (!available) throw new Error(`No variant for ${handle}`);
    return { id: available.id, title: product.title, handle: handle };
  }

  function computeDiscountPct(slotCount) {
    // Matches the live Shopify automatic discount: 20% off for 5+ items, none below.
    if (slotCount >= 5) return 20;
    return 0;
  }

  function buildSetSignature(picks) {
    return picks.map(p => (state.skus[p.hid] && state.skus[p.hid].h) || p.hid).sort().join('|').slice(0, 200);
  }

  // Core: add picks to Shopify cart. Returns true on success.
  async function addPicksToShopifyCart(picks) {
    const handles = picks.map(p => {
      const sku = state.skus[p.hid];
      if (!sku || !sku.h) throw new Error(`Missing handle for SKU ${p.hid}`);
      return sku.h;
    });
    const variants = await Promise.all(handles.map(h => fetchVariantId(h)));
    const items = variants.map(v => ({ id: v.id, quantity: 1 }));

    const setName = (document.getElementById('cw-set-name') || {}).textContent || 'Curated Set';
    const discountPct = computeDiscountPct(picks.length);
    const sig = buildSetSignature(picks);
    const skinKey = (state.skin || []).join(',');
    const ageKey = state.age || '';
    const moodKey = state.mood || '';

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: {
          curated_set:          setName,
          curated_set_size:     String(picks.length),
          curated_discount_pct: String(discountPct),
          curated_skin:         skinKey,
          curated_age:          ageKey,
          curated_mood:         moodKey,
          curated_signature:    sig,
          curated_goal_hint:    (state._goalHint || ''),
          curated_source:       'wizard_v2'
        }
      }),
      credentials: 'same-origin'
    });

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin'
    });
    if (!addRes.ok) {
      const errBody = await addRes.text().catch(() => '');
      throw new Error(`cart/add failed (${addRes.status}): ${errBody.slice(0, 200)}`);
    }

    return { setName, discountPct, skinKey, ageKey, moodKey };
  }

  // Button handler 1: Add to Cart only (stay on wizard)
  async function handleAddToCart() {
    const picks = state.currentPicks.filter(p => !p.removed);
    if (!picks.length) { alert('Please pick at least 1 item.'); return; }

    const btn = document.getElementById('cw-add-to-cart');
    const checkoutBtn = document.getElementById('cw-checkout-now');
    const originalLabel = btn.textContent;
    btn.disabled = true;
    if (checkoutBtn) checkoutBtn.disabled = true;
    btn.textContent = 'Adding to cart...';

    try {
      const meta = await addPicksToShopifyCart(picks);
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'curated_set_added', ...meta, set_size: picks.length });
      }
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 2500);
      if (checkoutBtn) checkoutBtn.disabled = false;
    } catch (err) {
      console.error('[curation-wizard] add to cart failed', err);
      btn.disabled = false;
      btn.textContent = originalLabel;
      if (checkoutBtn) checkoutBtn.disabled = false;
      alert('Could not add to cart.\n\n' + (err.message || err));
    }
  }

  // Button handler 2: Checkout Now (add then redirect to /checkout)
  async function handleCheckoutNow() {
    const picks = state.currentPicks.filter(p => !p.removed);
    if (!picks.length) { alert('Please pick at least 1 item.'); return; }

    const btn = document.getElementById('cw-checkout-now');
    const cartBtn = document.getElementById('cw-add-to-cart');
    const originalLabel = btn.textContent;
    btn.disabled = true;
    if (cartBtn) cartBtn.disabled = true;
    btn.textContent = 'Preparing checkout...';

    try {
      const meta = await addPicksToShopifyCart(picks);
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'curated_set_checkout', ...meta, set_size: picks.length });
      }
      // Cart page first - buyer can adjust quantities, see shipping preview, apply codes
      window.location.href = '/cart';
    } catch (err) {
      console.error('[curation-wizard] checkout failed', err);
      btn.disabled = false;
      btn.textContent = originalLabel;
      if (cartBtn) cartBtn.disabled = false;
      alert('Could not start checkout.\n\n' + (err.message || err));
    }
  }

  document.getElementById('cw-add-to-cart').addEventListener('click', handleAddToCart);

  const checkoutBtn = document.getElementById('cw-checkout-now');
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', handleCheckoutNow);
  }

})();
